import { Component, OnInit, OnDestroy, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";

import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { CoreConfigService } from "@core/services/config.service";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CustomerService } from "app/main/services/customer.service";
import { ThemeConfigService } from "app/main/services/theme-config.service";
import { ToastrService } from "ngx-toastr";
import { convertToUUID } from "app/main/helpers/binaryData";

@Component({
  selector: "core-theme-customizer",
  templateUrl: "./theme-customizer.component.html",
  styleUrls: ["./theme-customizer.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CoreThemeCustomizerComponent implements OnInit, OnDestroy {
  navbarColorValue: string;
  sidebarColorValue: string;
  coreConfig: any;
  form: FormGroup;
  company: any;
  public primary: string;
  public secondary: string;
  public hoverprimary: string;
  public hoversecondary: string;
  public info: string;
  public convertToUUID = convertToUUID;
  public warning: string;
  public theme_config: any;
  public light: string;
  public dark: string;
  public custom: string;
  public inputDarkMode: string;
  public colorDarkMode: string;
  public fontColor: string;
  public linkColor: string;
  public success: string;
  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor

   * @param {FormBuilder} _formBuilder
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * */
  constructor(
    private _formBuilder: FormBuilder,
    private _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _customerService: CustomerService,
    private _themeConfig: ThemeConfigService,
    private _toastrService: ToastrService
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  //  Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.primary = "#24bdd7";
    this.secondary = "#f27922";
    this.hoverprimary = "#198395";
    this.hoversecondary = "#d5600d";
    this.info = "#3264DC";
    this.warning = "#ffaa32";
    this.light = "#f6f6f6";
    this.dark = "#c0c0c0";
    this.custom = "#00ff00";
    this.success = "#37C846";
    this.inputDarkMode = "#C6C8CE";
    this.colorDarkMode = "#283046";
    // Build theme config form
    this.form = this._formBuilder.group({
      app: this._formBuilder.group({
        appName: new FormControl(),
        appTitle: new FormControl(),
        appLogoImage: new FormControl(),
        appLanguage: new FormControl(),
      }),
      layout: this._formBuilder.group({
        skin: new FormControl(),
        type: new FormControl(),
        animation: new FormControl(),
        menu: this._formBuilder.group({
          hidden: new FormControl(),
          collapsed: new FormControl(),
          backgroundColor: new FormControl(),
        }),
        navbar: this._formBuilder.group({
          hidden: new FormControl(),
          type: new FormControl(),
          background: new FormControl(),
          customBackgroundColor: new FormControl(),
          backgroundColor: new FormControl(),
        }),
        footer: this._formBuilder.group({
          hidden: new FormControl(),
          type: new FormControl(),
          background: new FormControl(),
          customBackgroundColor: new FormControl(),
          backgroundColor: new FormControl(),
        }),
        enableLocalStorage: new FormControl(),
        customizer: new FormControl(),
        scrollTop: new FormControl(),
        buyNow: new FormControl(),
      }),
    });

    this._customerService.getCompany().then(
      ({ model }) => {
        this.company = model;
        console.log(this.company);
        if (this.company.theme_comfig_id !== null) {
          this._themeConfig.getById(this.company.theme_config_id).subscribe(
            (model) => {
              this.primary = model.primary_color;
              this.secondary = model.secondary_color;
              this.hoverprimary = model.hoverprimary_color;
              this.hoversecondary = model.hoversecondary_color;
              this.info = model.info_color;
              this.warning = model.warning_color;

              const root: any = document.querySelector(":root");

              root.style.setProperty("--primary", this.primary);
              root.style.setProperty("--hoverprimary", this.hoverprimary);
              root.style.setProperty("--secondary", this.secondary);
              root.style.setProperty("--hoversecondary", this.hoversecondary);
              root.style.setProperty("--info", this.info);
              root.style.setProperty("--warning", this.warning);
            },
            (err: any) => console.error("error al obtener customers", err)
          );
        }
      },
      (err: any) => console.error("error al obtener customers", err)
    );

    // Subscribe to the config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        // Update config
        this.coreConfig = config;

        // Set the config form values
        this.form.setValue(config, { emitEvent: false });
      });

    // Subscribe to the form layout.type value changes
    this.form
      .get("layout.type")
      .valueChanges.pipe(takeUntil(this._unsubscribeAll))
      .subscribe((value) => {
        this._resetFormValues(value);
      });

    // Subscribe to the form value changes
    this.form.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this._coreConfigService.config = config;
      });

    // Set navbar color
    this.navbarColor(this.form.get("layout.navbar.backgroundColor").value);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  //  Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Reset form values based on the selected menu layout
   *
   * @param value
   * @private
   */
  private _resetFormValues(value): void {
    switch (value) {
      case "vertical": {
        this.form.patchValue({
          layout: {
            // skin: 'default',
            animation: "fadeIn",
            menu: {
              hidden: false,
              collapsed: false,
              backgroundColor: "",
            },
            navbar: {
              hidden: false,
              type: "floating-nav",
              background: "navbar-light",
              customBackgroundColor: true,
              backgroundColor: "",
            },
            footer: {
              hidden: false,
              type: "footer-static",
              background: "footer-light",
              customBackgroundColor: false,
              backgroundColor: "bg-primary",
            },
          },
        });
      }
      case "horizontal": {
        this.form.patchValue({
          layout: {
            // skin: 'default',
            animation: "fadeIn",
            menu: {
              hidden: false,
              collapsed: false,
              backgroundColor: "",
            },
            navbar: {
              hidden: false,
              type: "floating-nav",
              background: "navbar-light",
              customBackgroundColor: true,
              backgroundColor: "",
            },
            footer: {
              hidden: false,
              type: "footer-static",
              background: "footer-light",
              customBackgroundColor: false,
              backgroundColor: "bg-primary",
            },
          },
        });
      }
    }

    // Set navbar color
    this.navbarColor(this.form.get("layout.navbar.backgroundColor").value);
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Patch selected navbar color value to form
   *
   * @param value
   */
  navbarColor(value): void {
    this.navbarColorValue = value;
    this.form.patchValue({
      layout: {
        navbar: {
          customBackgroundColor: true,
          backgroundColor: this.navbarColorValue,
        },
      },
    });
  }

  hexToRgb(hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  public setColor(type: string, color: string) {
    const rgb = `${this.hexToRgb(color).r} ${this.hexToRgb(color).g} ${
      this.hexToRgb(color).b
    }`;
    const darken = color.substring(0, color.length - 2);
    const dark = `${darken}22`;
    console.log(dark);
    const root: any = document.querySelector(":root");
    if (!root.style?.hasOwnProperty(0)) {
      console.log(root.style[0]);
    } else {
      console.log("No hay");
    }

    switch (type) {
      case "primary":
        root.style.setProperty("--primary", color);
        root.style.setProperty("--rgbprimary", rgb);
        root.style.setProperty("--darkprimary", dark);
        console.log(root.style);
        this.primary = color;
        break;
      case "hoverprimary":
        root.style.setProperty("--hoverprimary", color);
        this.hoverprimary = color;
        break;
      case "secondary":
        root.style.setProperty("--secondary", color);
        root.style.setProperty("--rgbsecondary", rgb);
        root.style.setProperty("--darksecondary", dark);
        this.secondary = color;
        if (root.style?.hasOwnProperty(0)) {
          console.log(root.style[0]);
        } else {
          console.log("No hay");
        }
        break;
      case "hoversecondary":
        root.style.setProperty("--hoversecondary", color);
        this.hoversecondary = color;
        break;
      case "info":
        root.style.setProperty("--info", color);
        this.info = color;
        break;
      case "warning":
        root.style.setProperty("--warning", color);
        this.warning = color;
        break;
      default:
        break;
    }
  }
  onSubmit() {
    const conf = {
      primary_color: this.primary,
      secondary_color: this.secondary,
      info_color: this.info,
      warning_color: this.warning,
      hoverprimary_color: this.hoverprimary,
      hoversecondary_color: this.hoversecondary,
    };
    if (this.company.theme_config_id === null) {
      this._themeConfig.create(conf).subscribe(
        ({ model }) => {
          console.log(model);
          const themeId = this.convertToUUID(model.id);
          const companyId = this.convertToUUID(this.company.id);
          this.company.theme_config_id = themeId;
          this._customerService
            .updateCompanyTheme({ theme_config_id: themeId }, companyId)
            .subscribe(
              (res) => {
                console.log(res);
                this._toastrService.success("Nueva Configuración de Colores");
              },
              (err: any) => console.error("error al obtener customers", err)
            );
        },
        (err: any) => console.error("error al obtener customers", err)
      );
    } else {
      this._themeConfig.update(this.company.theme_config_id, conf).subscribe(
        (model) => {
          console.log(model);
          this._toastrService.success("Configuración Actualizada");
        },
        (err: any) => console.error("error al obtener customers", err)
      );
    }
  }
  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }
}
